import React, { useEffect } from 'react';
import qs from 'query-string';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  FREEMIUM_EVENT_PURCHASE_MODAL,
  FreemiumEventPurchaseModal,
} from '../../../pro/components/FreemiumEventPurchase';
import EventSelection from '../../../pro/views/NewContent/components/EventSelection';
import useCloseModal from '../../../../hooks/useCloseModal';
import useEventAddonPurchase from '../../../pro/components/FreemiumEventPurchase/hooks/useEventAddonPurchase';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../constants/event-items';
import { ALBUM_SUBTYPE } from '../../../../constants/albums';
import {
  isGroupVideoOnlyPurchase,
  isMemoryBookOnlyPurchase,
  isWaldofiedOnlyPurchase,
} from './helpers';
import MarcomLayout from '../../../../components/global/MarcomLayout';
import WaldoSpeechBubble from '../../../../components/global/WaldoSpeechBubble';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};

const SignupAlbumSelect = ({ history, location }) => {
  const {
    skipUpsell = false,
    includePhotosAlbum,
    selectedEventAddonItemTypes = [],
    eventAddonItemTypesUpsell = [],
    selectedEventAddonItemIds = [],
    promoCode = '',
    eventType: defaultEventType,
  } = qs.parse(location.search, {
    arrayFormat: 'bracket',
  });

  const {
    eventAddonItems = [],
    loadingEventAddonItems,
  } = useEventAddonPurchase({
    defaultEventAddonItemTypes: [
      EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
    ],
  });

  const selectedEventAddonItems = eventAddonItems?.filter(eventAddonItem =>
    selectedEventAddonItemTypes.includes(eventAddonItem.types[0]),
  );

  const eventCreateUrl = '/signup/albums/create?';
  const closeModal = useCloseModal();

  const onCreateEvent = ({ eventType, replaceUrl }) => {
    closeModal(FREEMIUM_EVENT_PURCHASE_MODAL);

    const onCreateUrl = `${eventCreateUrl}${qs.stringify(
      {
        eventType,
        promoCode,
        selectedEventAddonItemTypes,
        eventAddonItemTypesUpsell,
        // Pass these old props to skip upsell on wizard.
        includePhotosAlbum:
          includePhotosAlbum ||
          (skipUpsell
            ? selectedEventAddonItemTypes.includes(
                EVENT_ADDON_ITEM_TYPE.WALDOFIED,
              )
            : eventAddonItemTypesUpsell?.includes(
                EVENT_ADDON_ITEM_TYPE.WALDOFIED,
              )) ||
          undefined, // Avoids sending false.
        selectedEventAddonItemIds: (() => {
          if (selectedEventAddonItemIds?.length > 0) {
            return selectedEventAddonItemIds;
          }

          if (skipUpsell) {
            return selectedEventAddonItems.map(
              eventAddonItem => eventAddonItem.id,
            );
          }

          return undefined;
        })(),
      },
      { arrayFormat: 'bracket' },
    )}`;

    if (replaceUrl) {
      history.replace(onCreateUrl);
    } else {
      history.push(onCreateUrl);
    }
  };

  useEffect(() => {
    if (!loadingEventAddonItems && defaultEventType) {
      onCreateEvent({ eventType: defaultEventType, replaceUrl: true });
    }
  }, [defaultEventType, loadingEventAddonItems]);

  if (loadingEventAddonItems || defaultEventType) {
    return <FullScreenLoader />;
  }

  const isWaldofiedOnlyPurchaseBool = isWaldofiedOnlyPurchase(
    selectedEventAddonItemTypes,
  );
  const isMemoryBookOnlyPurchaseBool = isMemoryBookOnlyPurchase(
    selectedEventAddonItemTypes,
  );
  const isGroupVideoOnlyPurchaseBool = isGroupVideoOnlyPurchase(
    selectedEventAddonItemTypes,
  );

  const eventSelectionClasses = classNames({
    'pb-0': true,
  });

  const signupWrapperClasses = classNames({
    'weddings-signup': true,
    'memory-book-only-signup': isMemoryBookOnlyPurchaseBool,
    'waldofied-only-signup': isWaldofiedOnlyPurchaseBool,
    'group-video-only-signup': isGroupVideoOnlyPurchaseBool,
  });

  return (
    <MarcomLayout.Wrapper
      className={signupWrapperClasses}
      title={
        <>
          Seamlessly share event photos &<br />
          videos with Waldo!
        </>
      }
      signupDisabled
    >
      <WaldoSpeechBubble className="w-100-i mx-0 mb-3" position="right">
        What type of event are you hosting?
      </WaldoSpeechBubble>
      <EventSelection
        className={eventSelectionClasses}
        onEventClick={({ eventType }) => {
          switch (eventType) {
            default:
              onCreateEvent({
                eventType,
              });
              break;
          }
        }}
        shouldSkipEvent={albumSubtype => {
          if (
            [ALBUM_SUBTYPE.VACATIONS, ALBUM_SUBTYPE.FUNDRAISERS].includes(
              albumSubtype,
            )
          ) {
            return true;
          }

          if (isMemoryBookOnlyPurchaseBool) {
            return albumSubtype === ALBUM_SUBTYPE.FUNDRAISERS;
          }

          if (isGroupVideoOnlyPurchaseBool) {
            return [ALBUM_SUBTYPE.MEMORIALS, ALBUM_SUBTYPE.WEDDINGS].includes(
              albumSubtype,
            );
          }

          return false;
        }}
      />
      <FreemiumEventPurchaseModal />
    </MarcomLayout.Wrapper>
  );
};

SignupAlbumSelect.propTypes = propTypes;

export default SignupAlbumSelect;
